import React from 'react';
import {
  Box,
  Skeleton,
  Paper,
  Stack,
} from '@mui/material';
import { themeColors } from '../../utils/theme';

// Base skeleton props for consistent theming
const baseSkeletonProps = {
  sx: {
    bgcolor: `${themeColors.accent}40`, // Semi-transparent accent color
    '&::after': {
      background: `linear-gradient(90deg, transparent, ${themeColors.primary}20, transparent)`,
    },
  },
};

/**
 * Skeleton loader for table/data grid content
 */
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: themeColors.paper,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton
            key={`header-${index}`}
            variant="text"
            width={`${100 / columns}%`}
            height={40}
            {...baseSkeletonProps}
          />
        ))}
      </Box>
      
      {/* Data Rows */}
      <Stack spacing={1} sx={{ flex: 1 }}>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box key={`row-${rowIndex}`} sx={{ display: 'flex', gap: 2 }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                variant="text"
                width={`${100 / columns}%`}
                height={32}
                {...baseSkeletonProps}
              />
            ))}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

/**
 * Skeleton loader for card-based content
 */
export const CardSkeleton: React.FC<{ hasImage?: boolean; lines?: number }> = ({
  hasImage = false,
  lines = 3,
}) => {
  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: themeColors.paper,
        borderRadius: 2,
      }}
    >
      {hasImage && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          sx={{ mb: 2, borderRadius: 1, ...baseSkeletonProps.sx }}
        />
      )}
      
      <Stack spacing={1}>
        <Skeleton
          variant="text"
          width="60%"
          height={32}
          {...baseSkeletonProps}
        />
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton
            key={`line-${index}`}
            variant="text"
            width={index === lines - 1 ? '40%' : '100%'}
            height={24}
            {...baseSkeletonProps}
          />
        ))}
      </Stack>
    </Paper>
  );
};

/**
 * Skeleton loader for form fields
 */
export const FormSkeleton: React.FC<{ fields?: number; hasButtons?: boolean }> = ({
  fields = 3,
  hasButtons = true,
}) => {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        {Array.from({ length: fields }).map((_, index) => (
          <Box key={`field-${index}`}>
            <Skeleton
              variant="text"
              width="30%"
              height={24}
              sx={{ mb: 1, ...baseSkeletonProps.sx }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={56}
              sx={{ borderRadius: 1, ...baseSkeletonProps.sx }}
            />
          </Box>
        ))}
        
        {hasButtons && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Skeleton
              variant="rectangular"
              width={100}
              height={40}
              sx={{ borderRadius: 1, ...baseSkeletonProps.sx }}
            />
            <Skeleton
              variant="rectangular"
              width={120}
              height={40}
              sx={{ borderRadius: 1, ...baseSkeletonProps.sx }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Skeleton loader for list items
 */
export const ListSkeleton: React.FC<{ items?: number; hasAvatar?: boolean }> = ({
  items = 5,
  hasAvatar = false,
}) => {
  return (
    <Stack spacing={2}>
      {Array.from({ length: items }).map((_, index) => (
        <Box
          key={`list-item-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            backgroundColor: themeColors.paper,
            borderRadius: 1,
          }}
        >
          {hasAvatar && (
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              {...baseSkeletonProps}
            />
          )}
          
          <Box sx={{ flex: 1 }}>
            <Skeleton
              variant="text"
              width="70%"
              height={24}
              sx={{ mb: 1, ...baseSkeletonProps.sx }}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={20}
              {...baseSkeletonProps}
            />
          </Box>
          
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{ borderRadius: 1, ...baseSkeletonProps.sx }}
          />
        </Box>
      ))}
    </Stack>
  );
};

/**
 * Skeleton loader for text content blocks
 */
export const TextSkeleton: React.FC<{ 
  lines?: number; 
  hasTitle?: boolean;
  variant?: 'paragraph' | 'article' | 'short';
}> = ({
  lines = 4,
  hasTitle = true,
  variant = 'paragraph',
}) => {
  const getLineWidth = (index: number, total: number) => {
    if (variant === 'short') return '100%';
    if (variant === 'article') {
      if (index === total - 1) return '60%';
      return Math.random() > 0.3 ? '100%' : '90%';
    }
    // paragraph
    if (index === total - 1) return '70%';
    return '100%';
  };

  return (
    <Box>
      {hasTitle && (
        <Skeleton
          variant="text"
          width="50%"
          height={36}
          sx={{ mb: 2, ...baseSkeletonProps.sx }}
        />
      )}
      
      <Stack spacing={1}>
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton
            key={`text-line-${index}`}
            variant="text"
            width={getLineWidth(index, lines)}
            height={24}
            {...baseSkeletonProps}
          />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Skeleton loader for button loading states
 */
export const ButtonSkeleton: React.FC<{ 
  width?: number | string; 
  height?: number;
  variant?: 'contained' | 'outlined' | 'text';
}> = ({
  width = 120,
  height = 40,
  variant = 'contained',
}) => {
  const getBorderRadius = () => {
    switch (variant) {
      case 'outlined':
      case 'text':
        return 1;
      default:
        return 1;
    }
  };

  return (
    <Skeleton
      variant="rectangular"
      width={width}
      height={height}
      sx={{
        borderRadius: getBorderRadius(),
        ...baseSkeletonProps.sx,
      }}
    />
  );
};

/**
 * Full page skeleton loader
 */
export const PageSkeleton: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      {/* Page Title */}
      <Skeleton
        variant="text"
        width="40%"
        height={48}
        sx={{ mb: 3, ...baseSkeletonProps.sx }}
      />
      
      {/* Content Area */}
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 300px' }}>
        {/* Main Content */}
        <Box>
          <CardSkeleton lines={6} />
          <Box sx={{ mt: 3 }}>
            <TableSkeleton rows={8} columns={5} />
          </Box>
        </Box>
        
        {/* Sidebar */}
        <Box>
          <ListSkeleton items={4} hasAvatar />
        </Box>
      </Box>
    </Box>
  );
};